import { inject, Injectable } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { map, switchMap, shareReplay, distinctUntilChanged } from 'rxjs';
import { Memoize } from 'typescript-memoize';

import { filterBoolean } from '@core/common-legacy';
import {
  ReportsComponentsActions,
  ReportsComponentsSelectors,
  ReportsSelectors,
} from '@core/reports-data';
import type {
  ComponentFacadeParams,
  ReportDashboardComponent,
  ReportDashboardComponentCreate,
  ReportResults,
  ReportDashboardMetricOptions,
  ReportResultsDataParams,
} from '@core/reports-types';

import { ReportsConfigurationFacade } from './reports-configuration.facade';
import { ReportsResultsFacade } from './results.facade';

export const componentFacadeParamsMemoizeFn = (params: ComponentFacadeParams): string => {
  if ('component' in params) {
    const { component } = params;
    if ('id' in component) return `${component.id}`;
    if ('localId' in component) return `${component.localId}`;
    // better this than nothing. Only happens for view data, which should not be the case often
    return JSON.stringify(component);
  } else {
    return `${params.componentId}#${params.dashboardId}`;
  }
};

@Injectable({
  providedIn: 'root',
})
export class ReportComponentsDataFacade {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly reportsConfigFacade = inject(ReportsConfigurationFacade);
  private readonly resultsFacade = inject(ReportsResultsFacade);

  public onComponentCreate$: Observable<ReportDashboardComponentCreate> = this.actions$.pipe(
    ofType(ReportsComponentsActions.createComponent, ReportsComponentsActions.duplicateComponent),
    map(action => action.payload.data.component),
  );

  /**
   *
   * @param params - either a full component or componentId and dashboardId ONLY FOR components in store/components (not library-components)
   */
  @Memoize(componentFacadeParamsMemoizeFn)
  public getComponentDataParams(
    params: ComponentFacadeParams,
  ): Observable<ReportResultsDataParams> {
    if ('component' in params) {
      const { component } = params;
      return this.store
        .select(ReportsSelectors.createDataParamsForComponent(component))
        .pipe(filterBoolean, shareReplay(1));
    }
    const { dashboardId, componentId } = params;
    return this.store.pipe(
      select(
        dashboardId
          ? ReportsSelectors.componentDataParamsWithDashboardFiltersApplied(
              componentId,
              dashboardId,
            )
          : ReportsComponentsSelectors.componentDataParams(componentId),
      ),
      filterBoolean,
      distinctUntilChanged(),
      shareReplay(1),
    );
  }

  /**
   *
   * @param params - either a full component or componentId and dashboardId ONLY FOR components in store/components (not library-components)
   */
  @Memoize(componentFacadeParamsMemoizeFn)
  public getComponentRawData(params: ComponentFacadeParams): Observable<ReportResults> {
    return this.getComponentDataParams(params).pipe(
      switchMap(params => this.resultsFacade.getResults(params)),
      distinctUntilChanged((a, b) => (a && b ? a.checksum === b.checksum : false)),
      shareReplay(1),
    );
  }

  /**
   *
   * @param params - either a full component or componentId and dashboardId ONLY FOR components in store/components (not library-components)
   */
  @Memoize(componentFacadeParamsMemoizeFn)
  public getComponentRawDataMeta(params: ComponentFacadeParams): Observable<{
    isLoaded: boolean;
    isPending: boolean;
    isFailed: boolean;
    error: any;
  }> {
    return this.getComponentDataParams(params).pipe(
      switchMap(params => this.resultsFacade.getResultsMeta(params)),
      shareReplay(1),
    );
  }
  /**
   * @param componentId - component id. Only for entities, not library components
   * @param dashboardId - dashboard id. When provided, component will have dashboard filters applied
   */
  @Memoize((cid, did) => `${cid}#${did}`)
  public getComponent(
    componentId: number | string,
    dashboardId?: number | string,
  ): Observable<ReportDashboardComponent> {
    return this.store.pipe(
      select(ReportsSelectors.component(componentId, dashboardId)),
      filterBoolean,
    );
  }

  /**
   *
   * @param params - either a full component or componentId and dashboardId ONLY FOR components in store/components (not library-components)
   */
  @Memoize(componentFacadeParamsMemoizeFn)
  public getComponentMetric(
    params: ComponentFacadeParams,
  ): Observable<ReportDashboardMetricOptions> {
    if ('component' in params) {
      return this.reportsConfigFacade.getMetricBySlug(params.component.metric);
    }
    return this.store.pipe(
      select(ReportsSelectors.componentMetric(params.componentId)),
      filterBoolean,
      shareReplay(1),
    );
  }
}
